import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SemiconLayout from './SemiconLayout';
import { runPeel, mainEffects, savePeelSnapshot } from './peelModel';
import { PEEL_NOTES } from './peelNotes';
import NotesPanel from './NotesPanel';

function drawPeel(canvas, progress, peelN) {
  if (!canvas) return;
  const ctx = canvas.getContext('2d');
  const w = canvas.width;
  const h = canvas.height;
  ctx.clearRect(0, 0, w, h);
  const padY = h * 0.62;
  ctx.fillStyle = '#1e293b';
  ctx.fillRect(80, padY, w - 160, 36);
  ctx.fillStyle = '#64748b';
  ctx.fillRect(80, padY - 10, w - 160, 10);

  const lift = progress * 90;
  const split = 80 + (w - 220) * (0.35 + progress * 0.4);
  ctx.strokeStyle = '#f59e0b';
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.moveTo(80, padY - 10);
  ctx.lineTo(split, padY - 10);
  ctx.quadraticCurveTo(split + 30, padY - 10 - lift * 0.3, split + 70, padY - 10 - lift);
  ctx.stroke();

  ctx.strokeStyle = '#67e8f9';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(split + 70, padY - 10 - lift);
  ctx.lineTo(split + 70, padY - 10 - lift - 40 - peelN * 18);
  ctx.stroke();
  ctx.fillStyle = '#67e8f9';
  ctx.font = '700 22px Inter, sans-serif';
  ctx.fillText(`${peelN.toFixed(2)} N/mm`, split + 82, padY - 28 - lift);
}

const PeelShearBench = () => {
  const [cure, setCure] = useState(150);
  const [plasma, setPlasma] = useState('no');
  const [adhesive, setAdhesive] = useState('A');
  const [runs, setRuns] = useState([]);
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const canvasRef = useRef(null);
  const effects = mainEffects(runs);
  const last = runs[runs.length - 1];

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.parentElement.getBoundingClientRect();
    canvas.width = Math.max(640, rect.width * 2);
    canvas.height = 640;
    drawPeel(canvas, pull, last?.peel || 0);
  }, [pull, last]);

  useEffect(() => {
    savePeelSnapshot({
      runs: runs.map((r) => ({ cure: r.cure, plasma: r.plasma, adhesive: r.adhesive, peel: r.peel })),
      effects,
      lastPeel: last?.peel,
      lastPlasma: last?.plasma,
      n: runs.length,
      dominant: Object.entries(effects).sort((a, b) => Math.abs(b[1]) - Math.abs(a[1]))[0]?.[0],
    });
  }, [runs, effects, last]);

  const pullTest = () => {
    if (busy) return;
    setBusy(true);
    setPull(0);
    const result = runPeel({ cure, plasma, adhesive });
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / 900);
      setPull(p);
      if (p < 1) requestAnimationFrame(tick);
      else {
        setRuns((prev) => [...prev, result].slice(-10));
        setBusy(false);
      }
    };
    requestAnimationFrame(tick);
  };

  const maxEff = Math.max(0.01, ...Object.values(effects).map((v) => Math.abs(v)));

  return (
    <SemiconLayout
      kicker="Station 2 · die-attach peel"
      title="Pull it until it lets go"
      lead="You set the lot: cure, plasma, adhesive. The gripper pulls. Strength is not a sermon — it is a number that moves when the pad is clean."
    >
      <div className="sm-split">
        <div className="sm-controls">
          <div className="sm-toggles">
            <button type="button" className={`sm-toggle ${cure === 150 ? 'is-on' : ''}`} onClick={() => setCure(150)}>
              <strong>150 °C</strong><span>soft cure</span>
            </button>
            <button type="button" className={`sm-toggle ${cure === 180 ? 'is-on' : ''}`} onClick={() => setCure(180)}>
              <strong>180 °C</strong><span>full cure</span>
            </button>
            <button type="button" className={`sm-toggle ${plasma === 'yes' ? 'is-on' : ''}`} onClick={() => setPlasma(plasma === 'yes' ? 'no' : 'yes')}>
              <strong>Plasma {plasma}</strong><span>pad clean</span>
            </button>
          </div>
          <div className="sm-toggles" style={{ gridTemplateColumns: '1fr 1fr' }}>
            <button type="button" className={`sm-toggle ${adhesive === 'A' ? 'is-on' : ''}`} onClick={() => setAdhesive('A')}>
              <strong>Adhesive A</strong><span>standard DA</span>
            </button>
            <button type="button" className={`sm-toggle ${adhesive === 'B' ? 'is-on' : ''}`} onClick={() => setAdhesive('B')}>
              <strong>Adhesive B</strong><span>new chemistry</span>
            </button>
          </div>
          <button type="button" className="sm-btn" disabled={busy} onClick={pullTest}>
            {busy ? 'Pulling…' : 'Pull the joint'}
          </button>
          <p className="sm-note">Run both plasma states before you trust a main effect. Chemistry is the expensive guess.</p>
        </div>
        <div>
          <div className="sm-stage">
            <span className="sm-stage-tag">90° peel · live</span>
            <canvas ref={canvasRef} />
          </div>
          <div className="sm-readout">
            <strong>{last ? `${last.peel.toFixed(2)} N/mm` : '— N/mm'}</strong>
            {last && (
              <span className={`sm-spec ${last.plasma === 'yes' && last.peel > 0.55 ? 'sm-spec--ok' : last.plasma === 'no' ? 'sm-spec--fail' : 'sm-spec--watch'}`}>
                {last.plasma === 'no' ? 'likely interfacial (dirty pad)' : last.peel < 0.55 ? 'weak even clean — check cure' : 'likely cohesive / decent interface'}
              </span>
            )}
            <p className="sm-eq">mean ≈ 0.40 + 0.31·plasma + 0.055·cure + 0.025·B + noise</p>
            <p className="sm-note">
              {runs.length < 2
                ? 'One pull is an anecdote. Flip plasma and pull again before you blame the epoxy.'
                : Math.abs(effects.plasma) > Math.abs(effects.adhesive) + 0.05
                  ? 'Plasma owns this joint. A new DA qualification this week is theatre.'
                  : Math.abs(effects.cure) > Math.abs(effects.plasma)
                    ? 'Cure is moving you more than plasma — you may be under-converted, not on the wrong datasheet.'
                    : 'Effects are close. Widen the window or replicate before you spend.'}
            </p>
            {runs.length > 0 && (
              <div className="sm-bars">
                {['plasma', 'cure', 'adhesive'].map((k) => (
                  <div className="sm-bar-row" key={k}>
                    <span>{k}</span>
                    <div className="sm-bar"><i style={{ width: `${(Math.abs(effects[k]) / maxEff) * 100}%` }} /></div>
                    <span>{effects[k] >= 0 ? '+' : ''}{effects[k].toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
            {runs.length > 0 && (
              <table className="sm-table">
                <thead>
                  <tr><th>#</th><th>Cure</th><th>Plasma</th><th>DA</th><th>Peel</th></tr>
                </thead>
                <tbody>
                  {runs.map((r, i) => (
                    <tr key={r.id}>
                      <td>{i + 1}</td>
                      <td>{r.cure}</td>
                      <td>{r.plasma}</td>
                      <td>{r.adhesive}</td>
                      <td>{r.peel.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div style={{ marginTop: '0.9rem' }}>
              <Link to="/virtuallabs/semicon-mech/defence" className="sm-btn">Defend this lot →</Link>
            </div>
          </div>
        </div>
      </div>
      <NotesPanel title="Line notes — peel" items={PEEL_NOTES} />
    </SemiconLayout>
  );
};

export default PeelShearBench;
